/* =========================================================
   ESSE WERKS — GLOBAL JS ENTRY POINT
   Reserved for cross-page utilities (formatters, fetch helpers,
   AJAX cart hooks, telemetry). Section-specific JS lives in
   per-section files and is loaded only on pages that need it.
   Distinct filename from Dawn's own assets/global.js (kept intact).

   Loads at theme.liquid:175 — BEFORE ew-cart.js / tune-builder —
   so window.ewTrack is available to every later script.
   ========================================================= */
(function () {
  'use strict';

  /* ---------------------------------------------------------
     1. ewTrack — the GA4 contract every other lane calls.
     Consent is handled upstream by Consent Mode (gtag buffers /
     denies per the consent state set in theme.liquid). ewTrack
     does NOT re-implement consent; it only guards gtag presence,
     so a blank measurement ID or not-yet-loaded gtag is a clean
     no-op rather than a throw.
     --------------------------------------------------------- */
  window.ewTrack = function (eventName, params) {
    if (typeof window.gtag !== 'function') return; // blank ID / consent-not-loaded = clean no-op
    window.gtag('event', eventName, params || {});
  };

  /* Shopify line prices are integer cents; GA4 wants dollars. */
  function toDollars(cents) {
    var n = Number(cents);
    return isFinite(n) ? Math.round(n) / 100 : 0;
  }

  /* ---------------------------------------------------------
     1b. ewMoney — presentment-aware money formatter (INTL-2).

     Client-side prices in the Tune Builder + configurator are built
     in JS from cents that came from {{ variant.price }} (Liquid). Under
     Shopify Markets with currency conversion ON, those cents are ALREADY
     in the visitor's presentment currency (Shopify converts + rounds
     variant prices server-side per Market). The only thing JS must not
     hardcode is the *symbol/format* — otherwise an EU visitor sees a
     converted amount wearing a "$".

     window.ewMoneyFormat (injected in theme.liquid from shop.money_format)
     carries the active Market's format string, e.g. "${{amount}}" (USD,
     conversion off) or "€{{amount_with_comma_separator}}" (a EUR Market).
     We render the standard Shopify amount tokens against it. If the global
     is missing for any reason, we fall back to "$X,XXX" so we NEVER emit
     "$NaN" or an empty string — a clean USD render beats a broken one.

     opts.decimals — when set to false, decimals are stripped regardless of
     the format token (Tune Builder shows whole-dollar prices; the
     configurator passes nothing and keeps the format's own decimals). This
     keeps the US render at "$1,500" (not "$1,500.00") when conversion is OFF.
     --------------------------------------------------------- */
  function formatAmount(cents, decimals, useComma) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var value = n / 100;
    var fixed = decimals ? value.toFixed(2) : String(Math.round(value));
    var parts = fixed.split('.');
    var thousands = useComma ? '.' : ',';
    var dec = useComma ? ',' : '.';
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + dec + parts[1] : parts[0];
  }

  window.ewMoney = function (cents, opts) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0; /* guard: never "$NaN" */
    var fmt = typeof window.ewMoneyFormat === 'string' && window.ewMoneyFormat
      ? window.ewMoneyFormat
      : '${{amount}}'; /* USD fallback (Shopify default money_format) */
    /* opts.decimals === false forces whole-dollar even when the format
       token (e.g. {{amount}}) would include cents. undefined = honor token. */
    var forceNoDecimals = opts && opts.decimals === false;
    var out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':                                  return formatAmount(n, !forceNoDecimals, false);
        case 'amount_no_decimals':                      return formatAmount(n, false,            false);
        case 'amount_with_comma_separator':             return formatAmount(n, !forceNoDecimals, true);
        case 'amount_no_decimals_with_comma_separator': return formatAmount(n, false,            true);
        case 'amount_with_apostrophe_separator':        return formatAmount(n, !forceNoDecimals, false);
        default:                                        return formatAmount(n, !forceNoDecimals, false);
      }
    });
    /* If the format had no recognizable token, fall back so we never
       return the raw template or an empty string. */
    if (out === fmt) out = '$' + formatAmount(n, !forceNoDecimals, false);
    return out;
  };

  /* ---------------------------------------------------------
     2. add_to_cart — STANDARD product-form path via pub/sub.

     We subscribe to PUB_SUB_EVENTS.cartUpdate, which is published
     by several sources (see assets/*.js):
        product-form     → a real add (add.js response)
        quick-add        → a real add (bulk update.js response)
        cart-items       → a CART QUANTITY EDIT  (do not count)
        quick-order-list → a CART QUANTITY EDIT  (do not count)
     We allow-list only the real-add sources so quantity edits on
     the cart page / drawer do NOT fire a spurious add_to_cart.

     The Tune Builder + configurator add paths do NOT publish to
     pub/sub (liquid-theme instruments those separately), so this
     subscriber will not double-count them.
     --------------------------------------------------------- */
  var ADD_SOURCES = { 'product-form': true, 'quick-add': true };

  /* Pull a GA4 add_to_cart payload out of a cartUpdate event.
     Robust: never throws, returns null if it can't find a real add. */
  function buildAddToCartPayload(event) {
    if (!event || !ADD_SOURCES[event.source]) return null;

    var data = event.data || event; // pubsub passes the published object directly
    var cartData = data.cartData || {};

    /* The add.js / update.js response can carry the added line(s) as a
       top-level line object or as an `items` array. Normalize to a list. */
    var lines = [];
    if (Array.isArray(cartData.items)) {
      lines = cartData.items;
    } else if (cartData.id || cartData.variant_id) {
      lines = [cartData];
    }

    var wantId = data.productVariantId != null ? String(data.productVariantId) : null;
    var picked = null;

    if (wantId) {
      /* product-form gives us the exact variant added — match it so we
         don't mis-attribute value when the cart holds many lines. */
      for (var i = 0; i < lines.length; i++) {
        var lid = lines[i] && (lines[i].variant_id != null ? lines[i].variant_id : lines[i].id);
        if (lid != null && String(lid) === wantId) { picked = lines[i]; break; }
      }
    }

    var items = [];
    var value = 0;

    if (picked) {
      var qty = Number(picked.quantity) || 1;
      var linePrice = picked.final_line_price != null ? picked.final_line_price
                    : (picked.price != null ? picked.price * qty : 0);
      value = toDollars(linePrice);
      items.push({
        item_id: wantId,
        item_name: picked.product_title || picked.title || undefined,
        quantity: qty,
      });
    } else if (lines.length) {
      /* quick-add (no productVariantId): the response is the whole cart
         state. We can't isolate the delta reliably, so report the line
         items we have with item_id + quantity and sum a best-effort value.
         Keeps the event firing without fabricating a precise delta. */
      for (var j = 0; j < lines.length; j++) {
        var ln = lines[j] || {};
        var lqty = Number(ln.quantity) || 1;
        var lid2 = ln.variant_id != null ? ln.variant_id : ln.id;
        items.push({
          item_id: lid2 != null ? String(lid2) : undefined,
          item_name: ln.product_title || ln.title || undefined,
          quantity: lqty,
        });
      }
      value = toDollars(cartData.total_price);
    } else if (wantId) {
      /* Last resort: we know a variant was added but have no line detail. */
      items.push({ item_id: wantId, quantity: 1 });
    } else {
      return null;
    }

    return { currency: 'USD', value: value, items: items };
  }

  function wireAddToCart() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return; // pubsub globals absent
    if (!PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      try {
        var payload = buildAddToCartPayload(event);
        if (payload) window.ewTrack('add_to_cart', payload);
      } catch (_) { /* never break the cart on a tracking error */ }
    });
  }

  /* ---------------------------------------------------------
     3. begin_checkout — delegated listener on the checkout buttons.

     The cart-drawer button (snippets/cart-drawer.liquid:127) and the
     cart-page button (sections/main-cart-footer.liquid:110) both carry
     name="checkout". We listen (delegated on document) for click and
     submit, fetch a fresh /cart.js, build the GA4 payload, and fire.
     Fire-and-forget: gtag uses sendBeacon, so the event survives the
     navigation; we do not delay/block the checkout in any way.
     --------------------------------------------------------- */
  var beginCheckoutInFlight = false;

  function buildBeginCheckoutPayload(cart) {
    var items = [];
    var lines = (cart && Array.isArray(cart.items)) ? cart.items : [];
    for (var i = 0; i < lines.length; i++) {
      var ln = lines[i] || {};
      var id = ln.variant_id != null ? ln.variant_id : ln.id;
      items.push({
        item_id: id != null ? String(id) : undefined,
        item_name: ln.product_title || ln.title || undefined,
        price: toDollars(ln.price),
        quantity: Number(ln.quantity) || 1,
      });
    }
    return {
      currency: (cart && cart.currency) || 'USD',
      value: toDollars(cart && cart.total_price),
      items: items,
    };
  }

  function fireBeginCheckout() {
    if (beginCheckoutInFlight) return; // dedupe a click+submit double-tap
    beginCheckoutInFlight = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        window.ewTrack('begin_checkout', buildBeginCheckoutPayload(cart));
      })
      .catch(function () { /* /cart.js failed — no-op, never block checkout */ })
      .then(function () {
        /* release shortly after; navigation usually unloads us first */
        setTimeout(function () { beginCheckoutInFlight = false; }, 1500);
      });
  }

  function isCheckoutTrigger(target) {
    return !!(target && target.closest && target.closest('[name="checkout"]'));
  }

  function wireBeginCheckout() {
    document.addEventListener('click', function (e) {
      if (isCheckoutTrigger(e.target)) fireBeginCheckout();
    }, true);
    document.addEventListener('submit', function (e) {
      /* A form that contains the checkout button submitting also counts — but
         ONLY when checkout is what submitted it. The cart page puts the
         quantity +/- and Remove controls (name="updates[<key>]") in the SAME
         form#ew-cart-form as the checkout button, so matching on "the form
         contains [name=checkout]" alone reports a begin_checkout every time a
         shopper changes a quantity or removes a line.

         This was masked until TSA-UNGATE: ew-terms-gate.js used the identical
         predicate one listener earlier in the capture phase and called
         stopImmediatePropagation() on every such submit, so this handler never
         ran on cart mutations. Removing the gate un-masked it.

         e.submitter is the control that triggered the submit. When it is absent
         (programmatic form.submit(), very old browsers) fall back to the old
         behaviour rather than lose a real begin_checkout. */
      var form = e.target;
      if (!form || !form.querySelector) return;
      var submitter = e.submitter;
      if (submitter && submitter.name && submitter.name !== 'checkout') return;
      if (form.querySelector('[name="checkout"]')) fireBeginCheckout();
    }, true);
  }

  /* ---------------------------------------------------------
     4. Funnel events — delegated document listeners.

     The theme already dispatches these CustomEvents (verified in
     recon). We only LISTEN and translate to ewTrack. Each handler is
     try/wrapped so a missing detail/field never throws, and ewTrack
     itself no-ops if gtag is absent.
     --------------------------------------------------------- */

  /* 4a. livery_select — document event ew:livery-changed.
     detail = { livery, prev }. ew-livery.js:313/320 carry both;
     ew-garage-dialog.js:194 carries { livery } only (no prev) — so
     `previous` is allowed to be undefined. */
  function wireLiverySelect() {
    document.addEventListener('ew:livery-changed', function (e) {
      try {
        var d = (e && e.detail) || {};
        window.ewTrack('livery_select', {
          livery_id: d.livery,
          previous: d.prev,
        });
      } catch (_) { /* never break livery switching on a tracking error */ }
    });
  }

  /* 4b. vehicle_select — listen to ONLY ew:vehicle-resolved.
     This is the single canonical write funnel (ew-vehicle-resolver.js:68
     writeActive) that the Garage dialog AND homepage input both route
     through. We deliberately do NOT also subscribe to ew:vehicle-changed,
     which would double-count the same selection. detail = the vehicle
     object, canonicalized to carry chassis_code/engine_code (+ chassis/
     engine aliases), year, make, model, source, label. */
  function wireVehicleSelect() {
    document.addEventListener('ew:vehicle-resolved', function (e) {
      try {
        var v = (e && e.detail) || {};
        window.ewTrack('vehicle_select', {
          chassis_code: v.code || v.chassis_code || v.chassis,
          engine_code: v.engine_code || v.engine,
          year: v.year,
          make: v.make,
          model: v.model,
          source: v.source,
        });
      } catch (_) { /* never break vehicle resolution on a tracking error */ }
    });
  }

  /* 4c. save_vehicle — document event ew:vehicle-save-intent.
     detail = { email, vehicle } (ew-vehicle-resolver.js:488). We read
     vehicle fields ONLY and STRIP the email — never send PII to GA4. */
  function wireSaveVehicle() {
    document.addEventListener('ew:vehicle-save-intent', function (e) {
      try {
        var v = (e && e.detail && e.detail.vehicle) || {};
        window.ewTrack('save_vehicle', {
          chassis_code: v.code || v.chassis_code || v.chassis,
          engine_code: v.engine_code || v.engine,
          label: v.label,
        });
      } catch (_) { /* never break the save flow on a tracking error */ }
    });
  }

  /* --- Init --- */
  function init() {
    wireAddToCart();
    wireBeginCheckout();
    wireLiverySelect();
    wireVehicleSelect();
    wireSaveVehicle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
